// Live Chat Review (approved HTML:
// 13-Live-Chat-Consistency-Review-v5.html).
//
// Multi-chat workspace. Each conversation keeps its own draft, visitor
// context, unread count and verified-identity state. The Finish & Next
// footer stays disabled until the active chat has ended or been
// transferred; live sending is gated on LIVE_WRITE_GATE_LIVE_CHAT_SEND
// which is OFF at runtime, so the Blocked state is the production
// default.
//
// W31 (2026-10-20): on a real session the fixture conversations (Jamie,
// Sarah, Duke, Michael) are never the fallback.
//
// W37b (2026-10-20): GET /v1/live-chat/queue answers 200 since W37a
// (sinks/live-chat-queue-sink.mjs, READ; MEASURED with the url-bootstrap
// session: `{items: [], channelFilter: "originalChannelId=1000",
// openThreadsScanned: 0}`). `items[]` are HubSpot Conversations thread
// rows (id, status, latestMessageAt, assignedTo, contactId, contact{},
// identityText); the read carries thread metadata only, no messages, so
// the thread area says so instead of inventing a transcript. An empty
// queue says "No open chats"; the fixture strings in the components
// (sample capacity line, "Assigned to Jamie", "Sample") are passed in by
// the preview path only.
import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { WorkspaceChrome } from '../_shared/WorkspaceChrome.js';
import { DataBoundary, type BoundaryState } from '../_shared/DataBoundary.js';
import { ChatBar } from './components/ChatBar.js';
import { VisitorIdentity } from './components/VisitorIdentity.js';
import { ConversationCard } from './components/ConversationCard.js';
import { Composer } from './components/Composer.js';
import { VisitorContext } from './components/VisitorContext.js';
import { Icon } from '../_shared/IconSprite.js';
import { RepriorityPill } from '../_shared/PriorityFocus.js';
import {
  LIVE_CHAT_CAPACITY,
  LIVE_CHAT_CONVERSATIONS,
  type LiveChatConversation,
} from './mockData.js';
import { useScopedData } from '../_w2a_lower/scopedData.js';
import { isRecord, monthYear, readOptionalString, readString } from '../_w2a_lower/identity.js';
import './styles.css';

// Multi-chat queue payload. Each conversation carries its own identity,
// draft and unread count; the anonymous-to-customer transition is a
// visitor-identity field that the fixture already models.
interface LiveChatFixture {
  capacity: typeof LIVE_CHAT_CAPACITY;
  conversations: typeof LIVE_CHAT_CONVERSATIONS;
}

const LIVE_CHAT_FIXTURE: LiveChatFixture = {
  capacity: LIVE_CHAT_CAPACITY,
  conversations: LIVE_CHAT_CONVERSATIONS,
};

const EMPTY_TITLE = 'No open chats';
const EMPTY_BODY = 'Website visitors who start a chat appear here.';
const NOT_AVAILABLE = 'Not available';
const REAL_SIDE_NOTE = 'Rep availability is not connected yet';
const REAL_CAPACITY = 'Chat capacity not connected yet';
const REAL_THREAD_NOTE = 'Messages are not loaded in this workspace yet; this read carries the thread status only.';

/** A conversation plus the per-thread facts the W37b cards need. */
export type LiveConversation = LiveChatConversation & {
  assigned: { initials: string; label: string };
  statusLabel: string;
};

const FIXTURE_ASSIGNED = { initials: 'JD', label: 'Assigned to Jamie' };

function withFixtureFacts(convo: LiveChatConversation): LiveConversation {
  return { ...convo, assigned: FIXTURE_ASSIGNED, statusLabel: 'Connected' };
}

function hasQueueItems(body: unknown): boolean {
  return isRecord(body) && Array.isArray(body.items) && body.items.length > 0;
}

function titleCaseStatus(status: string | null): string {
  if (!status) return 'Open';
  const lower = status.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

/**
 * Map the real queue body (`items[]`, thread rows READ from
 * services/api-server/sinks/live-chat-queue-sink.mjs threadRow()) into the
 * conversation shape the cards render. Every field has a safe default so
 * an unexpected row never crashes the screen; rows without an id are
 * dropped. The read has no page context, typing state or messages, so
 * those fields say so rather than carry a sample.
 */
export function normalizeQueue(body: unknown): LiveConversation[] {
  const list = isRecord(body) && Array.isArray(body.items) ? body.items : [];
  const out: LiveConversation[] = [];
  for (const raw of list) {
    if (!isRecord(raw)) continue;
    const id = readString(raw.id) || readString(raw.conversationId);
    if (!id) continue;
    const contact = isRecord(raw.contact) ? raw.contact : null;
    const fullName = contact ? readOptionalString(contact.fullName) : null;
    const contactId = readOptionalString(raw.contactId) ?? (contact ? readOptionalString(contact.id) : null);
    const identityText = readOptionalString(raw.identityText);
    const label = fullName ?? identityText ?? (contactId ? `HubSpot contact ${contactId}` : 'Visitor');
    const latestMessageAt = readOptionalString(raw.latestMessageAt);
    const assignedTo = readOptionalString(raw.assignedTo);
    const status = readOptionalString(raw.status);
    out.push({
      id,
      label,
      previewName: label,
      identity: contactId ? 'verified' : 'anonymous',
      status: 'wait',
      unreadCount: 0,
      waitLabel: latestMessageAt ? `Last message ${monthYear(latestMessageAt) ?? latestMessageAt}` : 'No message time',
      draft: '',
      viewing: NOT_AVAILABLE,
      arrival: NOT_AVAILABLE,
      onSite: NOT_AVAILABLE,
      identityText: identityText ?? (contactId ? `HubSpot contact ${contactId}` : 'Identity not linked'),
      ...(contactId ? { verifiedCustomerId: contactId } : {}),
      thread: [],
      suggested: '',
      assigned: {
        initials: '',
        label: assignedTo ? `Assigned to HubSpot user ${assignedTo}` : 'Unassigned',
      },
      statusLabel: titleCaseStatus(status),
    });
  }
  return out;
}

export interface LiveChatScreenProps {
  stateOverride?: BoundaryState;
}

export function LiveChatScreen({ stateOverride }: LiveChatScreenProps = {}): ReactNode {
  const params = useParams();
  const location = useLocation();
  const idFromRoute = params.customerId;
  const scoped = useScopedData<unknown>({
    path: '/v1/live-chat/queue',
    fixture: LIVE_CHAT_FIXTURE,
    loadingLabel: 'Connecting to Live Chat…',
    errorTitle: 'Live Chat is offline',
    errorCode: 'LIVE_CHAT_UNAVAILABLE',
    isEmpty: body => !hasQueueItems(body),
    emptyTitle: EMPTY_TITLE,
    emptyBody: EMPTY_BODY,
    ...(stateOverride !== undefined ? { stateOverride } : {}),
  });
  const useFixture = scoped.previewAllowed;
  const conversations = useMemo<LiveConversation[]>(
    () => (useFixture ? LIVE_CHAT_CONVERSATIONS.map(withFixtureFacts) : normalizeQueue(scoped.data)),
    [useFixture, scoped.data],
  );
  const capacity = useFixture
    ? LIVE_CHAT_CAPACITY
    : (isRecord(scoped.data) && readString(scoped.data.capacity)) || REAL_CAPACITY;
  const openThreadsScanned =
    !useFixture && isRecord(scoped.data) && typeof scoped.data.openThreadsScanned === 'number'
      ? scoped.data.openThreadsScanned
      : null;
  const sideNote = useFixture
    ? 'No other reps available · Sample'
    : openThreadsScanned === null
      ? REAL_SIDE_NOTE
      : `${REAL_SIDE_NOTE} · ${openThreadsScanned} open HubSpot thread${openThreadsScanned === 1 ? '' : 's'} scanned`;
  const initialId = useMemo(() => {
    const match = conversations.find(c => c.verifiedCustomerId === idFromRoute);
    return match?.id ?? conversations[0]?.id ?? '';
  }, [idFromRoute, conversations]);

  const [activeId, setActiveId] = useState<string>(initialId);
  const [drafts, setDrafts] = useState<Record<string, string>>(() => {
    const seeded: Record<string, string> = {};
    for (const c of conversations) seeded[c.id] = c.draft;
    return seeded;
  });
  const [kind, setKind] = useState<BoundaryState['kind']>(
    stateOverride?.kind ?? 'default',
  );
  const state =
    stateOverride ??
    (scoped.state.kind === 'default' ? resolveState(kind, undefined) : scoped.state);

  const active =
    conversations.find(c => c.id === activeId) ?? conversations[0] ?? null;

  function handleUseSuggested(): void {
    if (!active) return;
    const convo = active;
    setDrafts(prev => ({ ...prev, [convo.id]: convo.suggested }));
  }

  const finishDisabled = true; // Only enabled after end/transfer at runtime.

  return (
    <WorkspaceChrome screen="live-chat" rail="my-work">
      {/* W40: the approved 13 has no breadcrumb row (MEASURED, 0 entries);
          the row stays only as the carrier of the preview label. */}
      {scoped.previewAllowed ? (
        <div className="breadcrumbs">
          <div>
            My work <span className="slash">/</span> <strong>Live Chat</strong>
          </div>
          <span className="preview">Design preview · Sample conversations · No live send</span>
        </div>
      ) : null}
      <DataBoundary state={state} testId="live-chat-boundary">
        {active ? (
          <>
            <ChatBar
              conversations={conversations}
              activeId={active.id}
              onSelect={setActiveId}
              capacity={capacity}
              sideNote={sideNote}
            />
            <VisitorIdentity convo={active} />
            <section className="hero">
              {/* W40: approved pill, informational, on every session. */}
              <div className="shared-repriority-heading">
                <RepriorityPill className="shared-repriority" />
              </div>
              <div className="herogrid">
                <div className="hero-item">
                  <div className="hero-icon">
                    <Icon id="message" />
                  </div>
                  <div>
                    <small>WHAT TO DO NOW</small>
                    <b id="task-title">Help this visitor choose the right product</b>
                  </div>
                </div>
                <div className="hero-item">
                  <Icon id="clock" />
                  <div>
                    <small>WHY NOW</small>
                    <p id="why-now">
                      {active.identity === 'anonymous'
                        ? 'Visitor is waiting in live chat.'
                        : `${active.previewName} is in live chat and needs help now.`}
                    </p>
                  </div>
                </div>
                <div className="hero-item">
                  <Icon id="target" />
                  <div>
                    <small>NEXT ACTION</small>
                    <p id="next-action">
                      {active.identity === 'anonymous'
                        ? 'Reply now. Identify only if helpful.'
                        : `Reply now using ${active.previewName.split(' ')[0]}’s account context.`}
                    </p>
                  </div>
                </div>
              </div>
            </section>
            <div className="columns">
              <div className="conversation">
                <ConversationCard
                  convo={active}
                  onCreateTicket={() => undefined}
                  onTransfer={() => undefined}
                  onEnd={() => undefined}
                  onUseSuggested={handleUseSuggested}
                  assigned={active.assigned}
                  statusLabel={active.statusLabel}
                  {...(useFixture ? {} : { emptyThreadNote: REAL_THREAD_NOTE })}
                />
                <Composer
                  draft={drafts[active.id] ?? ''}
                  onDraftChange={text => setDrafts(prev => ({ ...prev, [active.id]: text }))}
                  onSend={() => undefined}
                />
              </div>
              <VisitorContext convo={active} />
            </div>
          </>
        ) : (
          <section className="card" role="status" data-testid="live-chat-empty" style={{ padding: 24 }}>
            <h1>{EMPTY_TITLE}</h1>
            <p>{EMPTY_BODY}</p>
          </section>
        )}
      </DataBoundary>
      <footer className="footer">
        <button className="orange" id="finish" type="button" disabled={finishDisabled}>
          Finish &amp; Next →
        </button>
        <span id="finish-hint">{active ? 'Live chat in progress' : 'No live chat in progress'}</span>
      </footer>
      {scoped.previewAllowed && !stateOverride ? (
        <StatePreview kind={kind} onChange={setKind} />
      ) : null}
      {/* preserve router location in a hidden span so React Router sees it */}
      <span hidden data-pathname={location.pathname} />
    </WorkspaceChrome>
  );
}

function resolveState(
  kind: BoundaryState['kind'],
  override: BoundaryState | undefined,
): BoundaryState {
  if (override) return override;
  switch (kind) {
    case 'default':
      return { kind: 'default' };
    case 'loading':
      return { kind: 'loading', label: 'Connecting to Live Chat…' };
    case 'empty':
      return {
        kind: 'empty',
        title: EMPTY_TITLE,
        body: EMPTY_BODY,
      };
    case 'error':
      return {
        kind: 'error',
        title: 'Live Chat is offline',
        detail: 'The channel adapter did not respond.',
        errorCode: 'LIVE_CHAT_UNAVAILABLE',
      };
    case 'degraded':
      return {
        kind: 'degraded',
        title: 'Partial data',
        regions: [{ region: 'customer-identity', reason: 'identity_lookup_slow' }],
      };
    case 'blocked':
      return {
        kind: 'blocked',
        title: 'Live Chat send is paused',
        reason: 'Live-write gate OFF for Live Chat Send.',
        gateName: 'LIVE_WRITE_GATE_LIVE_CHAT_SEND',
      };
    case 'midcall':
      return {
        kind: 'midcall',
        label: 'Sending reply…',
        actionName: 'live_chat_send',
      };
    default:
      return { kind: 'default' };
  }
}

const BOUNDARY_KINDS: Array<BoundaryState['kind']> = [
  'default',
  'loading',
  'empty',
  'error',
  'degraded',
  'blocked',
  'midcall',
];

function StatePreview({
  kind,
  onChange,
}: {
  kind: BoundaryState['kind'];
  onChange: (k: BoundaryState['kind']) => void;
}): ReactNode {
  return (
    <div
      aria-label="Companion state preview"
      style={{
        marginTop: 16,
        padding: 10,
        border: '1px dashed #c4d4e5',
        borderRadius: 6,
        display: 'flex',
        gap: 6,
        flexWrap: 'wrap',
        background: '#f7fbff',
      }}
    >
      <span style={{ fontSize: 9, letterSpacing: 1, color: '#557193', fontWeight: 700 }}>
        PREVIEW STATE:
      </span>
      {BOUNDARY_KINDS.map(k => (
        <button
          key={k}
          type="button"
          onClick={() => onChange(k)}
          aria-pressed={kind === k}
          style={{
            fontSize: 10,
            padding: '4px 8px',
            borderRadius: 4,
            border: '1px solid #c4d4e5',
            background: kind === k ? '#175fac' : '#fff',
            color: kind === k ? '#fff' : '#305780',
            cursor: 'pointer',
          }}
        >
          {k}
        </button>
      ))}
    </div>
  );
}

export default LiveChatScreen;
